/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `hsa_accelerometer_data` message.
 *
 * Raw accelerometer data used for HSA custom data logging
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class HsaAccelerometerDataMesg : Mesg {
    public constructor() : super("HsaAccelerometerData", Profile.MesgNum.HSA_ACCELEROMETER_DATA)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** timestamp_ms, in ms. Millisecond resolution of the timestamp */
    public var timestampMs: UShort?
        get() = getFieldValue(TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TIMESTAMP_MS_FIELD_NUM, value)

    /** sampling_interval, in ms. Sampling Interval in Milliseconds */
    public var samplingInterval: UShort?
        get() = getFieldValue(SAMPLING_INTERVAL_FIELD_NUM) as? UShort
        set(value) = setFieldValue(SAMPLING_INTERVAL_FIELD_NUM, value)

    /** Number of values stored in accel_x. */
    public val numAccelX: Int
        get() = getField(ACCEL_X_FIELD_NUM)?.numValues ?: 0

    /** accel_x, in mG. X-Axis Measurement */
    public var accelX: List<Double?>?
        get() {
            val field = getField(ACCEL_X_FIELD_NUM) ?: return null
            return List(field.numValues) { getAccelX(it) }
        }
        set(value) {
            removeField(ACCEL_X_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAccelX(index, element) }
        }

    public fun getAccelX(index: Int): Double? =
        getFieldValue(ACCEL_X_FIELD_NUM, index) as? Double

    public fun setAccelX(index: Int, value: Double?) {
        setFieldValue(ACCEL_X_FIELD_NUM, value, index)
    }

    /** Number of values stored in accel_y. */
    public val numAccelY: Int
        get() = getField(ACCEL_Y_FIELD_NUM)?.numValues ?: 0

    /** accel_y, in mG. Y-Axis Measurement */
    public var accelY: List<Double?>?
        get() {
            val field = getField(ACCEL_Y_FIELD_NUM) ?: return null
            return List(field.numValues) { getAccelY(it) }
        }
        set(value) {
            removeField(ACCEL_Y_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAccelY(index, element) }
        }

    public fun getAccelY(index: Int): Double? =
        getFieldValue(ACCEL_Y_FIELD_NUM, index) as? Double

    public fun setAccelY(index: Int, value: Double?) {
        setFieldValue(ACCEL_Y_FIELD_NUM, value, index)
    }

    /** Number of values stored in accel_z. */
    public val numAccelZ: Int
        get() = getField(ACCEL_Z_FIELD_NUM)?.numValues ?: 0

    /** accel_z, in mG. Z-Axis Measurement */
    public var accelZ: List<Double?>?
        get() {
            val field = getField(ACCEL_Z_FIELD_NUM) ?: return null
            return List(field.numValues) { getAccelZ(it) }
        }
        set(value) {
            removeField(ACCEL_Z_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAccelZ(index, element) }
        }

    public fun getAccelZ(index: Int): Double? =
        getFieldValue(ACCEL_Z_FIELD_NUM, index) as? Double

    public fun setAccelZ(index: Int, value: Double?) {
        setFieldValue(ACCEL_Z_FIELD_NUM, value, index)
    }

    /** timestamp_32k. 32 kHz timestamp */
    public var timestamp32k: UInt?
        get() = getFieldValue(TIMESTAMP_32K_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TIMESTAMP_32K_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val TIMESTAMP_MS_FIELD_NUM: UByte = 0u
        public const val SAMPLING_INTERVAL_FIELD_NUM: UByte = 1u
        public const val ACCEL_X_FIELD_NUM: UByte = 2u
        public const val ACCEL_Y_FIELD_NUM: UByte = 3u
        public const val ACCEL_Z_FIELD_NUM: UByte = 4u
        public const val TIMESTAMP_32K_FIELD_NUM: UByte = 5u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("TimestampMs", 0u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[1] = Field("SamplingInterval", 1u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[2] = Field("AccelX", 2u, BaseType.SINT16, 1.024, 0.0, "mG", false)
            fields[3] = Field("AccelY", 3u, BaseType.SINT16, 1.024, 0.0, "mG", false)
            fields[4] = Field("AccelZ", 4u, BaseType.SINT16, 1.024, 0.0, "mG", false)
            fields[5] = Field("Timestamp32k", 5u, BaseType.UINT32, 1.0, 0.0, "", false)
            return fields
        }
    }
}
